import { type ReactNode, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { api } from "@/helper/api";
import { normalizeLmsStartVertragSelection } from "@/components/lms/lmsQuickSearchEligibility";
import { COURSE_ACCESS_PERMISSION } from "@/components/lms/lms.utils";
import {
  setManagerLmsAccessError,
  setManagerLmsAccessLoading,
  setManagerLmsAccessReady,
} from "@/components/state/lms/managerLmsAccessSlice";
import { RootState } from "@/components/state/store";

interface Props {
  children: ReactNode;
}

export default function PortalLmsAccessBoundary({ children }: Props) {
  const dispatch = useDispatch();
  const requestId = useRef(0);
  const permissions = useSelector(
    (state: RootState) => state.login.permissions
  );
  const loginSubjectId = useSelector(
    (state: RootState) => state.login.subjectId
  );
  const subjectId = String(loginSubjectId || "");
  const access = useSelector((state: RootState) => state.managerLmsAccess);
  const hasCoursePermission = permissions.includes(COURSE_ACCESS_PERMISSION);
  const accessMatchesSubject = access.subjectId === subjectId;
  const accessIsReady =
    access.status === "ready" &&
    accessMatchesSubject &&
    access.selection !== null;
  const hasPortalLmsAccess =
    accessIsReady &&
    access.selection?.selectable === true &&
    access.hasCourseEnrolments === true;

  useEffect(() => {
    if (
      !hasCoursePermission ||
      !subjectId ||
      accessIsReady ||
      ((access.status === "loading" || access.status === "error") &&
        accessMatchesSubject)
    ) {
      return;
    }

    const currentRequestId = ++requestId.current;
    dispatch(setManagerLmsAccessLoading(subjectId));
    api.genericApiRequest({
      entity: "me",
      method: "get",
      successHandler: (response: any) => {
        if (
          currentRequestId !== requestId.current ||
          String(response?.data?.subject_id ?? "") !== subjectId
        ) {
          return;
        }
        dispatch(
          setManagerLmsAccessReady({
            subjectId,
            selection: normalizeLmsStartVertragSelection(response.data),
            hasCourseEnrolments:
              response?.data?.has_course_enrolments === true,
          })
        );
      },
      failHandler: () => {
        if (currentRequestId === requestId.current) {
          dispatch(setManagerLmsAccessError(subjectId));
        }
      },
    });
  }, [
    access.status,
    accessIsReady,
    accessMatchesSubject,
    dispatch,
    hasCoursePermission,
    subjectId,
  ]);

  if (!hasCoursePermission) {
    return <Navigate to="/" replace />;
  }

  if (!accessIsReady) {
    return null;
  }

  return hasPortalLmsAccess ? <>{children}</> : <Navigate to="/" replace />;
}
